import React from 'react';
import {
  Box,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { optionToggleSx } from '../toggle-styles';
import { TherapistOption, TherapistScope } from './types';

const ANY = '';

const label = (t: TherapistOption) => {
  const name = `${t.lastName}, ${t.firstName}`;
  //  Two provider records can carry the same name. Showing the resident count beside it
  //  is the only thing that tells them apart.
  return t.nameIsAmbiguous
    ? `${name} — ${t.patientCount} residents (duplicate name)`
    : `${name} — ${t.patientCount} residents`;
};

interface SelectProps {
  therapists: TherapistOption[];
  loading: boolean;
  therapistUuid: string;
  onTherapistChange: (uuid: string) => void;
}

/**
 * SQ-171 / SQ-173 — the treating-therapist dropdown, laid out alongside payer type/name
 * rather than stacked below them. Split out from the scope toggle so the two can sit in
 * different rows.
 */
export const TherapistSelect: React.FC<SelectProps> = ({
  therapists,
  loading,
  therapistUuid,
  onTherapistChange,
}) => {
  const noneInWindow = !loading && therapists.length === 0;

  const control = (
    <TextField
      select
      size="small"
      fullWidth
      label="Treating therapist"
      InputLabelProps={{ shrink: true }}
      SelectProps={{ displayEmpty: true }}
      value={noneInWindow ? ANY : therapistUuid}
      disabled={noneInWindow}
      onChange={(e) => onTherapistChange(e.target.value)}
    >
      <MenuItem value={ANY}>Any</MenuItem>
      {therapists.map((t) => (
        <MenuItem
          key={t.uuid}
          value={t.uuid}
        >
          {label(t)}
        </MenuItem>
      ))}
    </TextField>
  );

  return noneInWindow ? (
    <Tooltip title="No therapists have documents in this window">
      <span style={{ width: '100%' }}>{control}</span>
    </Tooltip>
  ) : (
    control
  );
};

interface ScopeProps {
  therapists: TherapistOption[];
  therapistUuid: string;
  scope: TherapistScope;
  onScopeChange: (scope: TherapistScope) => void;
  /**
   * Documents each scope would return. They are fetched independently — sharing one
   * Redux slice made both show the same number and follow the selected tab.
   */
  authorCount: number | null;
  chartsCount: number | null;
}

export const TherapistScopeToggle: React.FC<ScopeProps> = ({
  therapists,
  therapistUuid,
  scope,
  onScopeChange,
  authorCount,
  chartsCount,
}) => {
  const chosen = therapists.find((t) => t.uuid === therapistUuid);

  return (
    <Box>
      <Typography
        variant="caption"
        color={therapistUuid ? 'text.secondary' : 'text.disabled'}
        sx={{ display: 'block', mb: 0.75 }}
      >
        Which of their documents
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        //  Nothing selected until a therapist is named, not merely greyed out. The group
        //  defaults to `author` in the panel's state, and rendering that as the pressed
        //  option told the reader a choice had been made for a question that was never
        //  asked -- while `therapistScope` is omitted from the payload entirely in this
        //  state. A disabled control showing a live selection is a claim the request does
        //  not make.
        value={therapistUuid ? scope : null}
        //  Inert until a therapist is named — the question has no meaning for "Any".
        disabled={!therapistUuid}
        onChange={(_e, next) => next && onScopeChange(next as TherapistScope)}
        aria-label="Which of their documents"
        sx={optionToggleSx}
      >
        <ToggleButton value="author">
          Only notes they wrote{authorCount !== null && ` · ${authorCount}`}
        </ToggleButton>
        <ToggleButton value="charts">
          All documents for their patients{chartsCount !== null && ` · ${chartsCount}`}
        </ToggleButton>
      </ToggleButtonGroup>
      {chosen && scope === 'charts' && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.75 }}
        >
          Includes other clinicians&apos; notes on charts {chosen.lastName} worked, within these
          dates only.
        </Typography>
      )}
    </Box>
  );
};
